import { render } from 'preact';
import { effect } from '@preact/signals';
import { registerSW } from 'virtual:pwa-register';
import { App } from './app';
import { tenant } from './lib/tenant';
import { lang } from './lib/i18n';
import { theme } from './ui/ThemeSwitch';
// Side-effect import: creates the `api` singleton for the configured VITE_BACKEND mode
// (in demo mode this lazily boots the in-browser backend on first call, not here).
import './lib/api';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/utilities.css';

document.documentElement.lang = lang.value;

// Tenant theme overrides win over packages/brand/tokens.css because an inline style on :root
// always beats a stylesheet rule, whatever selector that stylesheet uses — which is exactly the
// trap for `--ink`/`--primary`/`--primary-strong`: `config/tenant.json`'s `theme` block only ships
// ONE (light) set of these, so setting them unconditionally would permanently pin the light
// values and defeat tokens.css's own `:root[data-theme="dark"]` / `prefers-color-scheme: dark`
// steps for the rest of the app's lifetime, in every theme. `--accent` is identical in both of
// tokens.css's modes, so it's always safe to override; the other three are skipped while dark is
// active and re-applied whenever the resolved theme changes (the toggle, or the OS setting when
// on "system").
const rootStyle = document.documentElement.style;

function resolvedThemeIsDark(): boolean {
  if (theme.value === 'dark') return true;
  if (theme.value === 'light') return false;
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function applyTenantTheme() {
  rootStyle.setProperty('--accent', tenant.theme.accent);
  if (resolvedThemeIsDark()) {
    rootStyle.removeProperty('--primary');
    rootStyle.removeProperty('--primary-strong');
    rootStyle.removeProperty('--ink');
  } else {
    rootStyle.setProperty('--primary', tenant.theme.primary);
    rootStyle.setProperty('--primary-strong', tenant.theme.primaryStrong);
    rootStyle.setProperty('--ink', tenant.theme.ink);
  }
}

applyTenantTheme();
effect(() => {
  void theme.value; // re-run whenever the theme signal changes (system/light/dark toggle)
  applyTenantTheme();
});
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTenantTheme);
}

if ('serviceWorker' in navigator) {
  // autoUpdate: the new service worker activates and reloads without asking — appropriate for a
  // small, frequently-iterated civic-tech app with no unsaved-work risk at the shell level
  // (forms themselves persist drafts to IndexedDB regardless).
  registerSW({ immediate: true });
}

const container = document.getElementById('app');
if (!container) throw new Error('#app root element is missing from index.html');
render(<App />, container);

// The critical inline splash (see index.html) is no longer needed once Preact has taken over.
document.getElementById('splash')?.remove();

// Marks the app as mounted so layout.css's `.fade-up` entrance animation can play (see the
// comment there) — content stays fully visible if this line never runs.
document.documentElement.classList.add('motion-ready');
